import { AccountApplicationCategoryNameDto, AccountDocumentTypeDto } from 'src/dtos';
import { Maybe } from 'src/utils';

import { EntityAccountType } from './accounts.models';
import { InvestigationStatus } from './investigation.model';

export class AccountApplicationCategoryName {
  private _value: AccountApplicationCategoryNameDto;
  private _label?: string;

  constructor(state: AccountApplicationCategoryNameDto, entityAccountType?: EntityAccountType) {
    this._value = state;
    this._label = this.findLabel(entityAccountType);
  }

  private findApplicationCategoryLabel(entityAccountType: EntityAccountType): Maybe<string> {
    if (entityAccountType.isCorporate) {
      return 'Corporate Application';
    }

    if (entityAccountType.isLlc) {
      return 'Account Application';
    }

    if (entityAccountType.isPartnership) {
      return 'Partnership Account Application';
    }

    if (entityAccountType.isPersonalTrust) {
      return 'Personal Trust Account Application';
    }

    if (entityAccountType.isBusinessTrust) {
      return 'Business Trust Account Application';
    }
  }

  private findLabel(entityAccountType?: EntityAccountType): Maybe<string> {
    if (this.isApplication && entityAccountType) {
      return this.findApplicationCategoryLabel(entityAccountType);
    }

    if (this.isArticlesOfIncorporation) {
      return 'Certified Articles of Incorporation';
    }

    if (this.isArticlesOfOrganization) {
      return 'Certified Articles of Organization';
    }

    if (this.isOperatingAgreement) {
      return 'Operating Agreement';
    }

    if (this.isPartnershipAgreement) {
      return 'Partnership Agreement';
    }

    if (this.isTrustAgreement) {
      return 'Trust Agreement';
    }

    if (this.isGovernmentId) {
      return 'Government issued photo ID(s)';
    }
  }

  get value(): AccountApplicationCategoryNameDto {
    return this._value;
  }

  get label(): Maybe<string> {
    return this._label;
  }

  get isGovernmentId(): boolean {
    return this._value === AccountApplicationCategoryNameDto.GovernmentId;
  }

  get isApplication(): boolean {
    return this._value === AccountApplicationCategoryNameDto.Application;
  }

  get isArticlesOfIncorporation(): boolean {
    return this._value === AccountApplicationCategoryNameDto.ArticlesOfIncorporation;
  }

  get isArticlesOfOrganization(): boolean {
    return this._value === AccountApplicationCategoryNameDto.ArticlesOfOrganization;
  }

  get isOperatingAgreement(): boolean {
    return this._value === AccountApplicationCategoryNameDto.OperatingAgreement;
  }

  get isPartnershipAgreement(): boolean {
    return this._value === AccountApplicationCategoryNameDto.PartnershipAgreement;
  }

  get isTrustAgreement(): boolean {
    return this._value === AccountApplicationCategoryNameDto.TrustAgreement;
  }
}

export enum AccountDocumentTypeLabel {
  CorporateApplication = 'Application',
  CorporateArticlesOfIncorporation = 'Articles Of Incorporation',
  LlcApplication = 'Application',
  LlcOperatingAgreement = 'Operating Agreement',
  LlcArticlesOfOrganization = 'Articles Of Organization',
  PartnershipApplication = 'Application',
  PartnershipAgreement = 'Agreement',
  TrustApplication = 'Application',
  TrustAgreement = 'Trust Agreement',
  DriverLicense = "Driver's License",
  Passport = 'Passport ID',
  StateIdcard = 'State ID Card',
}

export class AccountApplicationDocumentType {
  private _value: AccountDocumentTypeDto;
  private _label: AccountDocumentTypeLabel;

  constructor(state: AccountDocumentTypeDto) {
    this._value = state;
    this._label = this.findLabel();
  }

  private findLabel(): AccountDocumentTypeLabel {
    switch (this._value) {
      case AccountDocumentTypeDto.CorporateApplication:
        return AccountDocumentTypeLabel.CorporateApplication;
      case AccountDocumentTypeDto.CorporateArticlesOfIncorporation:
        return AccountDocumentTypeLabel.CorporateArticlesOfIncorporation;
      case AccountDocumentTypeDto.LlcApplication:
        return AccountDocumentTypeLabel.LlcApplication;
      case AccountDocumentTypeDto.LlcOperatingAgreement:
        return AccountDocumentTypeLabel.LlcOperatingAgreement;
      case AccountDocumentTypeDto.LlcArticlesOfOrganization:
        return AccountDocumentTypeLabel.LlcArticlesOfOrganization;
      case AccountDocumentTypeDto.PartnershipApplication:
        return AccountDocumentTypeLabel.PartnershipApplication;
      case AccountDocumentTypeDto.PartnershipAgreement:
        return AccountDocumentTypeLabel.PartnershipAgreement;
      case AccountDocumentTypeDto.TrustApplication:
        return AccountDocumentTypeLabel.TrustApplication;
      case AccountDocumentTypeDto.TrustAgreement:
        return AccountDocumentTypeLabel.TrustAgreement;
      case AccountDocumentTypeDto.CorporateOwnerDriverLicense:
      case AccountDocumentTypeDto.LlcBeneficialOwnerDriverLicense:
      case AccountDocumentTypeDto.PartnerDriverLicense:
      case AccountDocumentTypeDto.TrusteeDriverLicense:
        return AccountDocumentTypeLabel.DriverLicense;
      case AccountDocumentTypeDto.CorporateOwnerPassport:
      case AccountDocumentTypeDto.LlcBeneficialOwnerPassport:
      case AccountDocumentTypeDto.PartnerPassport:
      case AccountDocumentTypeDto.TrusteePassport:
        return AccountDocumentTypeLabel.Passport;
      case AccountDocumentTypeDto.CorporateOwnerStateIdCard:
      case AccountDocumentTypeDto.LlcBeneficialOwnerStateIdCard:
      case AccountDocumentTypeDto.PartnerStateIdCard:
      case AccountDocumentTypeDto.TrusteeStateIdCard:
        return AccountDocumentTypeLabel.StateIdcard;
    }
  }

  get value(): AccountDocumentTypeDto {
    return this._value;
  }

  get label(): AccountDocumentTypeLabel {
    return this._label;
  }
}

export interface AccountApplicationDocument {
  id: string;
  type: AccountApplicationDocumentType;
  originalFilename: string;
  createdAt: string;
  updatedAt?: string;
}

export interface AccountApplicationCategory {
  name: AccountApplicationCategoryName;
  isResolved: boolean;
  requestedDocuments: AccountApplicationDocumentType[];
  totalRequestedDocuments?: number;
  uploadedDocuments?: AccountApplicationDocument[];
  adminNote?: string;
}

export interface AccountApplicationCategoryView extends AccountApplicationCategory {
  key: string;
  children?: AccountApplicationCategoryView[];
}

export interface AccountApplication {
  id: string;
  status: InvestigationStatus; // TODO: rename InvestigationSTatus to AccountStatus
  account: {
    id: string;
    entityAccountType?: EntityAccountType;
  };
  categories: AccountApplicationCategory[];
  uploadedDocuments: AccountApplicationDocument[];
  createdAt: string;
  updatedAt?: string;
}

export interface DownloadAccountApplicationDocumentResponse {
  id: string;
  file: { url: string };
}
